import { create } from 'zustand';
import { api } from '@/lib/api';
import type { MarketingStrategy } from '@/types';
import type { ApiResponse } from '@/types';

const CURRENT_YEAR = new Date().getFullYear();

interface StrategyState {
  isPanelOpen: boolean;
  activeYear: number;
  strategy: MarketingStrategy | null;
  isLoading: boolean;
  error: string | null;

  openPanel: (year?: number) => Promise<void>;
  closePanel: () => void;
  togglePanel: () => Promise<void>;
  setStrategy: (strategy: MarketingStrategy | null) => void;
  loadStrategyForYear: (year: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useStrategyStore = create<StrategyState>((set, get) => ({
  isPanelOpen: false,
  activeYear: CURRENT_YEAR,
  strategy: null,
  isLoading: false,
  error: null,

  loadStrategyForYear: async (year: number) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<MarketingStrategy | null>>(
        `/marketing-strategies/active?year=${year}`
      );
      set({ strategy: res.data ?? null, activeYear: year });
    } catch {
      set({ strategy: null, error: 'Strategie konnte nicht geladen werden.' });
    } finally {
      set({ isLoading: false });
    }
  },

  openPanel: async (year?: number) => {
    const targetYear = year ?? get().activeYear;
    const currentStrategy = get().strategy;

    // Only reload if year changed or no strategy loaded yet
    if (!currentStrategy || currentStrategy.year !== targetYear) {
      await get().loadStrategyForYear(targetYear);
    } else if (year && year !== get().activeYear) {
      await get().loadStrategyForYear(targetYear);
    }

    set({ isPanelOpen: true, activeYear: targetYear });
  },

  closePanel: () => {
    set({ isPanelOpen: false });
  },

  togglePanel: async () => {
    if (get().isPanelOpen) {
      set({ isPanelOpen: false });
    } else {
      await get().openPanel();
    }
  },

  setStrategy: (strategy: MarketingStrategy | null) => {
    set({ strategy });
  },

  refresh: async () => {
    await get().loadStrategyForYear(get().activeYear);
  },
}));
